import { LOGIN_METHODS, type LoginMethod } from '@jupi/shared'
import { ApiError } from '../api/client'

// Small helpers shared by the sign-in pages and the profile's sign-in card.

// Which sign-in tab the login page opens on, remembered per device: the
// method last used here, or the one chosen on the profile. The server is
// never asked -- telling a stranger an address's method would say the
// address has an account.
const METHOD_KEY = 'jupi.loginMethod'

export function rememberedMethod(): LoginMethod {
  try {
    const stored = localStorage.getItem(METHOD_KEY)
    if (stored && (LOGIN_METHODS as readonly string[]).includes(stored)) return stored as LoginMethod
  } catch {
    // Storage blocked (a private window): fall through to the default.
  }
  return 'password'
}

export function rememberMethod(method: LoginMethod) {
  try {
    localStorage.setItem(METHOD_KEY, method)
  } catch {
    // Only a convenience.
  }
}

// The one-time token an emailed link carries in its fragment
// (#token=...). Read once, then wiped from the address bar so it does not
// linger in history, a bookmark or a screenshot. The fragment never
// reaches a server in the first place. Kept for the page that took it, so
// a second read -- React's StrictMode calls state initialisers twice --
// still gets it.
let taken: { path: string; token: string } | null = null

export function takeFragmentToken(): string | null {
  const path = window.location.pathname
  const match = window.location.hash.match(/token=([A-Za-z0-9_-]{43})/)
  if (window.location.hash) {
    window.history.replaceState(window.history.state, '', path + window.location.search)
  }
  if (match?.[1]) taken = { path, token: match[1] }
  return taken?.path === path ? taken.token : null
}

// The `code` a problem+json answer carries (see AUTH_PROBLEM_CODES), and
// for weak_password the `reason` -- a PasswordProblem.
export function problemCode(err: unknown): string | undefined {
  return problemField(err, 'code')
}

export function problemReason(err: unknown): string | undefined {
  return problemField(err, 'reason')
}

function problemField(err: unknown, field: 'code' | 'reason'): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  const value = (err.problem as Record<string, unknown> | null)?.[field]
  return typeof value === 'string' ? value : undefined
}

// A WebAuthn call the member cancelled, or let time out, rather than one
// that failed: the browser reports both as NotAllowedError.
export function isWebAuthnCancel(err: unknown): boolean {
  return err instanceof Error && err.name === 'NotAllowedError'
}
